import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import axiosInstance from "../../../config/axiosConfig";
import { normalizeRut } from "../../../utils/bonos";

// Modal para cargar (o resubir/reemplazar) un bono masivo sobre Honorarios.
// Descarga un template (RUT + Nombre + Monto Bruto vacío) a partir de los auxiliares
// del periodo actual, y sube ese mismo template relleno para crear el bono.
const BonosCargaModal = ({ visible, onClose, periodo, tipo, resultadosBase, editingBono, onSuccess }) => {
    const [glosa, setGlosa] = useState("");
    const [archivo, setArchivo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [resultado, setResultado] = useState(null);

    useEffect(() => {
        if (visible) {
            setGlosa(editingBono?.glosa || "");
            setArchivo(null);
            setError(null);
            setResultado(null);
        }
    }, [visible, editingBono]);

    if (!visible) return null;

    const handleDescargarTemplate = () => {
        const filas = (resultadosBase || []).map(row => ({
            RUT: row.rut || "",
            Nombre: `${row.nombre_auxiliar || ""} ${row.apepat_auxiliar || ""} ${row.apemat_auxiliar || ""}`.trim(),
            "Monto Bruto": "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(filas);
        worksheet["!cols"] = [{ wch: 16 }, { wch: 35 }, { wch: 14 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Bonos");
        XLSX.writeFile(workbook, `Template_Bonos_${tipo}_${periodo || "periodo"}.xlsx`);
    };

    const parseArchivo = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    const filas = XLSX.utils.sheet_to_json(sheet, { defval: "" });

                    const items = filas
                        .map(fila => {
                            const claves = Object.keys(fila);
                            const claveRut = claves.find(k => k.toLowerCase().includes("rut"));
                            const claveMonto = claves.find(
                                k => k.toLowerCase().includes("bruto") || k.toLowerCase().includes("monto")
                            );
                            const rut = claveRut ? String(fila[claveRut]).trim() : "";
                            const monto = claveMonto ? Number(fila[claveMonto]) || 0 : 0;
                            return { rut, monto_bruto: monto };
                        })
                        .filter(item => normalizeRut(item.rut) && item.monto_bruto > 0);

                    resolve(items);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });

    const handleCargar = async () => {
        if (!glosa.trim()) {
            setError("La glosa es obligatoria.");
            return;
        }
        if (!archivo) {
            setError("Debes seleccionar el template con los montos.");
            return;
        }

        setLoading(true);
        setError(null);
        setResultado(null);

        try {
            const items = await parseArchivo(archivo);
            if (!items.length) {
                setError("No se encontraron filas válidas (RUT + Monto Bruto) en el archivo.");
                setLoading(false);
                return;
            }

            const resp = editingBono
                ? await axiosInstance.post(`/liquidacion/bono/${editingBono.id}/reemplazar`, { glosa, items })
                : await axiosInstance.post("/liquidacion/bono/cargar", { periodo, tipo, glosa, items });

            if (resp.data?.error) {
                throw new Error(resp.data.message || "No se pudo cargar el bono.");
            }

            setResultado(resp.data.data);
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || "Error al cargar el bono.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header bg-success">
                        <h5 className="modal-title text-white">
                            <i className="bi bi-gift me-2"></i>
                            {editingBono ? `Resubir Bono - ${editingBono.glosa}` : "Cargar Bono Masivo"}
                        </h5>
                        <button className="btn-close" onClick={onClose} disabled={loading}></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted small">
                            Periodo <strong>{periodo}</strong> · {tipo === "adelanto" ? "Adelanto" : "Fin de Mes"}
                        </p>

                        <div className="mb-3">
                            <label className="form-label fw-bold">Glosa</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ej: Aguinaldo Fiestas Patrias"
                                value={glosa}
                                onChange={e => setGlosa(e.target.value)}
                                disabled={loading}
                            />
                            <small className="text-muted">
                                Este texto aparecerá como columna en la tabla y en el resumen de pago.
                            </small>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-bold">1. Descarga el template</label>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={handleDescargarTemplate}
                                    disabled={!resultadosBase?.length}
                                >
                                    <i className="bi bi-file-earmark-excel me-2"></i>
                                    Descargar Template ({resultadosBase?.length || 0} auxiliares)
                                </button>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-bold">2. Sube el mismo template con los montos</label>
                            <input
                                type="file"
                                className="form-control"
                                accept=".xlsx,.xls,.csv"
                                onChange={e => setArchivo(e.target.files?.[0] || null)}
                                disabled={loading}
                            />
                        </div>

                        {error && <div className="alert alert-danger">{error}</div>}

                        {resultado && (
                            <div className="alert alert-success">
                                <div>Bono cargado para <strong>{resultado.creados}</strong> auxiliar(es).</div>
                                {resultado.no_encontrados?.length > 0 && (
                                    <div className="mt-2 text-danger">
                                        No se encontraron {resultado.no_encontrados.length} RUT(s): {resultado.no_encontrados.join(", ")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            {resultado ? "Cerrar" : "Cancelar"}
                        </button>
                        {!resultado && (
                            <button className="btn btn-success" onClick={handleCargar} disabled={loading}>
                                {loading ? "Cargando..." : (editingBono ? "Reemplazar" : "Cargar Bono")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BonosCargaModal;
